import { mapToColumnsAndRows } from '@/helpers/utils'
import type { PropsWithChildren } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

type TargetElement = 'a' | 'button' // TODO: update targetElement as needed

type FocusManagerProps = PropsWithChildren<{
  className?: string
  initialFocusIndex: number
  rows: number[]
  targetElements: readonly TargetElement[] // TODO: update targetElement as needed
}>

/**
 * - act as a focus manager for the rows
 * - `initialFocusIndex` - the index of the element to focus
 * - `rows` - the focusable row grid i.e. [3,3,3,3,2,2]
 * - `targetElement` - the tagname of the elements to focus
 */
export const FocusManager = ({
  children,
  className,
  initialFocusIndex,
  rows,
  targetElements,
}: FocusManagerProps) => {
  // FIXME: add logic to skip focus, tabIndex, data-index on disabled elements

  const containerRef = useRef<HTMLDivElement | null>(null)
  const currentFocusedElementRef = useRef<HTMLElement | null>(null)
  const focusableElementsRef = useRef<HTMLElement[]>([])

  const mapRowColIndex = useMemo(() => {
    return mapToColumnsAndRows(rows)
  }, [rows])

  const setFocus = useCallback((element: HTMLElement) => {
    // set the tabIndex to 0 for the focused element
    focusableElementsRef.current.forEach(element => {
      element.tabIndex = -1
    })
    element.tabIndex = 0
    currentFocusedElementRef.current = element
    currentFocusedElementRef.current?.focus()
  }, [])

  const getCurrentElementPos = (element: HTMLElement) => {
    // get index of the current focused element
    const index = element.getAttribute('data-index')
    const currentElementPos = mapRowColIndex[Number(index)]
    if (!currentElementPos) {
      throw new Error('Current element position not found')
    }
    return currentElementPos
  }

  // on mount get all the focusable elements, apply tabIndex, data-row, set focus
  useEffect(() => {
    if (!containerRef.current) return

    // get all the focusable elements inside the container
    const focusableElements = containerRef.current.querySelectorAll(
      targetElements.join(', '),
    )

    if (!focusableElements.length) {
      return
    }

    const focusableElementsArray = Array.from(
      focusableElements,
    ) as HTMLElement[]

    focusableElementsRef.current = focusableElementsArray

    const handleFocus = (event: FocusEvent) => {
      setFocus(event.currentTarget as HTMLElement)
    }

    // set the tabIndex and data-row attribute, and focus listener
    focusableElementsRef.current.forEach((element, index) => {
      const result = mapRowColIndex[index]
      if (!result) {
        throw new Error('Row and column index not found')
      }
      const isInitialFocus = index === initialFocusIndex

      element.setAttribute('data-index', result.index.toString())
      element.tabIndex = isInitialFocus ? 0 : -1

      if (isInitialFocus) {
        currentFocusedElementRef.current = element
      }

      element.addEventListener('focus', handleFocus)
    })

    return () => {
      // clean up the focusable elements
      focusableElementsArray.forEach(element => {
        element.removeAttribute('data-index')
        element.removeAttribute('tabIndex')
        element.removeEventListener('focus', handleFocus)
      })
    }
  }, [initialFocusIndex, mapRowColIndex, targetElements, setFocus])

  const handleArrowUp = () => {
    if (!currentFocusedElementRef.current) {
      throw new Error('Current focused element not found')
    }
    let nextElement: HTMLElement = currentFocusedElementRef.current
    // get the current focused element
    const currentElementPos = getCurrentElementPos(
      currentFocusedElementRef.current,
    )

    // check if no row above
    if (currentElementPos.row - 1 < 0) {
      // do nothing
      return nextElement
    }

    // get the row and find the element above the current element
    const aboveElementPos = mapRowColIndex.find(
      item =>
        item.row === currentElementPos.row - 1 &&
        item.col === currentElementPos.col,
    )
    if (aboveElementPos) {
      const aboveElement = focusableElementsRef.current[aboveElementPos.index]
      if (aboveElement) {
        nextElement = aboveElement
      }
    } else {
      // find next closest element in the same row
      const nextClosestElementPos = mapRowColIndex[currentElementPos.index - 1]
      if (nextClosestElementPos) {
        const nextClosestElement =
          focusableElementsRef.current[nextClosestElementPos.index]
        if (nextClosestElement) {
          nextElement = nextClosestElement
        }
      }
    }

    return nextElement
  }

  const handleArrowDown = () => {
    if (!currentFocusedElementRef.current) {
      throw new Error('Current focused element not found')
    }
    let nextElement: HTMLElement = currentFocusedElementRef.current
    // get the current focused element
    const currentElementPos = getCurrentElementPos(
      currentFocusedElementRef.current,
    )

    // check if no row below
    if (currentElementPos.row + 1 >= rows.length) {
      // do nothing
      return nextElement
    }

    // get the row and find the element below the current element
    const belowElementPos = mapRowColIndex.find(
      item =>
        item.row === currentElementPos.row + 1 &&
        item.col === currentElementPos.col,
    )
    if (belowElementPos) {
      const belowElement = focusableElementsRef.current[belowElementPos.index]
      if (belowElement) {
        nextElement = belowElement
      }
    } else {
      // find next closest element in the same row
      const nextClosestElementPos = mapRowColIndex[currentElementPos.index + 1]
      if (nextClosestElementPos) {
        const nextClosestElement =
          focusableElementsRef.current[nextClosestElementPos.index]
        if (nextClosestElement) {
          nextElement = nextClosestElement
        }
      }
    }

    return nextElement
  }

  const handleArrowLeft = () => {
    if (!currentFocusedElementRef.current) {
      throw new Error('Current focused element not found')
    }
    let nextElement: HTMLElement = currentFocusedElementRef.current
    // get the current focused element
    const currentElementPos = getCurrentElementPos(
      currentFocusedElementRef.current,
    )

    // check if no column left
    if (currentElementPos.col - 1 < 0) {
      // do nothing
      return nextElement
    }

    // get the row and find the element left of the current element
    const leftElementPos = mapRowColIndex.find(
      item =>
        item.row === currentElementPos.row &&
        item.col === currentElementPos.col - 1,
    )
    if (leftElementPos) {
      const leftElement = focusableElementsRef.current[leftElementPos.index]
      if (leftElement) {
        nextElement = leftElement
      }
    }

    return nextElement
  }

  const handleArrowRight = () => {
    if (!currentFocusedElementRef.current) {
      throw new Error('Current focused element not found')
    }
    let nextElement: HTMLElement = currentFocusedElementRef.current
    // get the current focused element
    const currentElementPos = getCurrentElementPos(
      currentFocusedElementRef.current,
    )

    // check if no column right
    if (currentElementPos.col + 1 >= rows.length) {
      // do nothing
      return nextElement
    }
    // get the row and find the element right of the current element
    const rightElementPos = mapRowColIndex.find(
      item =>
        item.row === currentElementPos.row &&
        item.col === currentElementPos.col + 1,
    )

    if (rightElementPos) {
      const rightElement = focusableElementsRef.current[rightElementPos.index]
      if (rightElement) {
        nextElement = rightElement
      }
    }
    return nextElement
  }

  // handle the keyDown event for arrow keys
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const key = event.key

    let nextElement: HTMLElement | null = null
    switch (key) {
      case 'ArrowUp': {
        nextElement = handleArrowUp()
        break
      }
      case 'ArrowDown': {
        nextElement = handleArrowDown()
        break
      }
      case 'ArrowLeft': {
        nextElement = handleArrowLeft()
        break
      }
      case 'ArrowRight': {
        nextElement = handleArrowRight()
        break
      }
    }
    if (nextElement) {
      event.preventDefault()
      setFocus(nextElement)
      nextElement.focus()
    }
  }

  return (
    <div className={className} onKeyDown={handleKeyDown} ref={containerRef}>
      {children}
    </div>
  )
}
